import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useRouter } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Clock, ShieldAlert } from 'lucide-react';
import { AlertBanner, Badge, Button, FieldError, Input, Label } from '@/components/ui';
import { AuthShell } from '@/components/AuthShell';
import { partnerApi } from '@/lib/api-affiliate';
import { ApiError } from '@/lib/api';

const schema = z.object({
  email: z.string().email('Некорректный email'),
  password: z.string().min(6, 'Минимум 6 символов'),
});
type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const router = useRouter();
  const login = partnerApi.auth.useLogin();
  const [state, setState] = useState<'idle' | 'pending' | 'blocked'>('idle');
  const [credentialError, setCredentialError] = useState<string | null>(null);

  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (values: FormValues) => {
    setCredentialError(null);
    try {
      await login.mutateAsync(values);
      toast.success('Добро пожаловать!');
      router.navigate({ to: '/' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        if (err.code === 'PENDING_REVIEW') setState('pending');
        else if (err.code === 'BLOCKED') setState('blocked');
        else setCredentialError(err.message || 'Доступ запрещён');
      } else if (err instanceof ApiError) {
        setCredentialError('Неверный email или пароль');
      } else {
        setCredentialError('Не удалось войти. Попробуйте ещё раз.');
      }
    }
  };

  if (state === 'pending') {
    return (
      <AuthShell>
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-[var(--warning-soft)] grid place-items-center text-[var(--warning)] mb-4">
            <Clock size={24} />
          </div>
          <Badge tone="warning" className="mb-3">На рассмотрении</Badge>
          <h1 className="text-[22px] font-semibold mb-2 text-[var(--text)] tracking-tight">
            Заявка отправлена
          </h1>
          <p className="text-sm text-[var(--text-muted)] mb-6 leading-relaxed">
            Ваша заявка ещё рассматривается администратором. Проверьте почту — мы напишем сразу, как одобрим аккаунт.
          </p>
          <Button onClick={() => setState('idle')} variant="outline" className="w-full">
            Вернуться к входу
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (state === 'blocked') {
    return (
      <AuthShell>
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-[var(--danger-soft)] grid place-items-center text-[var(--danger)] mb-4">
            <ShieldAlert size={24} />
          </div>
          <Badge tone="danger" className="mb-3">Заблокирован</Badge>
          <h1 className="text-[22px] font-semibold mb-2 text-[var(--text)] tracking-tight">
            Аккаунт заблокирован
          </h1>
          <p className="text-sm text-[var(--text-muted)] mb-6 leading-relaxed">
            Если считаете это ошибкой, свяжитесь с поддержкой — мы разберёмся.
          </p>
          <Button onClick={() => setState('idle')} variant="outline" className="w-full">
            Назад
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Вход в кабинет"
      subtitle="Партнёрская программа Nexus CRM"
      footer={
        <>
          Нет аккаунта?{' '}
          <Link to="/register" className="text-[var(--accent-bright)] hover:underline font-medium">
            Регистрация
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {credentialError && <AlertBanner tone="danger">{credentialError}</AlertBanner>}
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            {...register('email')}
          />
          <FieldError message={formState.errors.email?.message} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label htmlFor="password">Пароль</Label>
            <Link
              to="/reset-password"
              className="text-xs text-[var(--text-muted)] hover:text-[var(--accent-bright)]"
            >
              Забыли пароль?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            {...register('password')}
          />
          <FieldError message={formState.errors.password?.message} />
        </div>
        <Button
          type="submit"
          loading={login.isPending}
          className="w-full"
          variant="gradient"
          size="lg"
        >
          {login.isPending ? 'Вход…' : 'Войти'}
        </Button>
      </form>
    </AuthShell>
  );
}
